/**
 * Theme adapter for WSJ US site — injects real next/font families into the foundation.
 */
import type { Theme } from '@emotion/react';
import { buildTheme } from 'common-ui';
import type { CommonThemeVars, ThemeCreateFunction } from 'tsconfig/types';
import { escrowbannerFont, escrowFont } from '../common/nextFont';
import {
  buildThemeVarsUSWSJ as buildThemeVarsUSWSJFoundation,
  buildThemeMixinsUSWSJ as buildThemeMixinsUSWSJFoundation,
  buildThemeWidgetStylesUSWSJ as buildThemeWidgetStylesUSWSJFoundation,
  buildThemeWidgetStylesUSWSJFromBase,
  buildThemeWidgetStylesUsLWFromBase,
  buildCoreWidgets,
  buildProductCardWidgets,
  buildProductWidgets,
  buildSearchWidgets,
  buildMiniCartWidgets,
  buildCartWidgets,
  buildAccountWidgets,
  buildLandingpageWidgets,
} from './themeFoundation';

export const buildThemeVarsUSWSJ = (
  vars: Pick<Theme, CommonThemeVars>,
): ReturnType<typeof buildThemeVarsUSWSJFoundation> =>
  buildThemeVarsUSWSJFoundation(vars, {
    escrow: escrowFont.style.fontFamily,
    escrowbanner: escrowbannerFont.style.fontFamily,
  });

export const buildThemeMixinsUSWSJ = (
  vars: Omit<Theme, 'mixins' | 'widgets'>,
): ReturnType<typeof buildThemeMixinsUSWSJFoundation> => buildThemeMixinsUSWSJFoundation(vars);

export const buildThemeWidgetStylesUSWSJ = (
  vars: Omit<Theme, 'widgets'>,
): ReturnType<typeof buildThemeWidgetStylesUSWSJFoundation> => buildThemeWidgetStylesUSWSJFoundation(vars);

// CMS pages: core + product cards + minicart (header)
export const createCmsPageTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsUSWSJ, buildThemeMixinsUSWSJ, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesUSWSJFromBase(
      vars,
      buildThemeWidgetStylesUsLWFromBase(vars, {
        ...buildCoreWidgets(vars),
        ...buildProductCardWidgets(vars),
        ...buildMiniCartWidgets(vars),
      }),
    ),
  );

// PDP: core + full product + minicart (header)
export const createProductTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsUSWSJ, buildThemeMixinsUSWSJ, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesUSWSJFromBase(
      vars,
      buildThemeWidgetStylesUsLWFromBase(vars, {
        ...buildCoreWidgets(vars),
        ...buildProductWidgets(vars),
        ...buildMiniCartWidgets(vars),
      }),
    ),
  );

// Search/PLP: core + search + product cards + minicart
export const createSearchTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsUSWSJ, buildThemeMixinsUSWSJ, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesUSWSJFromBase(
      vars,
      buildThemeWidgetStylesUsLWFromBase(vars, {
        ...buildCoreWidgets(vars),
        ...buildSearchWidgets(vars),
        ...buildProductWidgets(vars),
        ...buildMiniCartWidgets(vars),
      }),
    ),
  );

// Cart: core + product cards + cart
export const createCartTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsUSWSJ, buildThemeMixinsUSWSJ, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesUSWSJFromBase(
      vars,
      buildThemeWidgetStylesUsLWFromBase(vars, {
        ...buildCoreWidgets(vars),
        ...buildProductCardWidgets(vars),
        ...buildCartWidgets(vars),
      }),
    ),
  );

// Account: core + account + minicart (header)
export const createAccountTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsUSWSJ, buildThemeMixinsUSWSJ, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesUSWSJFromBase(
      vars,
      buildThemeWidgetStylesUsLWFromBase(vars, {
        ...buildCoreWidgets(vars),
        ...buildAccountWidgets(vars),
        ...buildMiniCartWidgets(vars),
      }),
    ),
  );

// Landing/offer pages: core + landing page + minicart
export const createLandingPageTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsUSWSJ, buildThemeMixinsUSWSJ, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesUSWSJFromBase(
      vars,
      buildThemeWidgetStylesUsLWFromBase(vars, {
        ...buildCoreWidgets(vars),
        ...buildLandingpageWidgets(vars),
        ...buildMiniCartWidgets(vars),
      }),
    ),
  );

const createTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsUSWSJ, buildThemeMixinsUSWSJ, buildThemeWidgetStylesUSWSJ);
export default createTheme;
